import { formatCopy } from "../../theme/copy";
import { Box, Typography } from "@mui/material";
import { ActionButton, CommonImageComp, Hints } from "../../components/ui";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import type { ConjointDefinition, ConjointDraft, ConjointEvent } from "./core";
import {
  actionButtonWrapper,
  card,
  cardAttribute,
  cardAttributeLabel,
  cardMedia,
  conjointContainer,
  fixedOptionsContainer,
  landscapeCell,
  landscapeConceptHeader,
  landscapeRowHeader,
  landscapeScrollContainer,
  landscapeTable,
  portraitCards,
} from "./styles";

type ViewProps = QuestionViewProps<
  ConjointDefinition,
  ConjointDraft,
  ConjointEvent
>;

export const ConjointView = (props: ViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const { definition, draft } = props;
  const wide = props.display.arrangement === "wide";

  const conceptMedia = (index: number) => {
    const concept = definition.concepts[index];
    if (!concept.image) return null;
    return (
      <Box sx={cardMedia}>
        <CommonImageComp
          currentVal={{
            image: concept.image,
            alt_text:
              concept.description ||
              (concept.displayLabel && concept.label) ||
              formatCopy(theme.copy.labels.option, { index: index + 1 }),
          }}
          imageStyle={{ width: "100%", maxHeight: "120px" }}
          imageTestid={`conjoint-option-image-${index}`}
        />
      </Box>
    );
  };

  const choose = (index: number) => {
    const concept = definition.concepts[index];
    const selected = draft.selectedId === concept.id;
    return (
      <Box sx={actionButtonWrapper(theme)}>
        <ActionButton
          currentVal={{ text: theme.copy.actions.choose, display_text: true }}
          testId={`conjoint-option-${index}`}
          disabled={concept.disabled || !props.controls.canSubmit}
          isSelected={selected}
          ariaLabel={formatCopy(theme.copy.labels.chooseOption, {
            index: index + 1,
          })}
          ariaPressed={selected}
          onClick={() => props.submit({ type: "select", value: concept.id })}
        />
      </Box>
    );
  };

  return (
    <Box data-testid="conjoint-view" sx={conjointContainer(theme, wide)}>
      {wide ? (
        <Box
          data-testid="conjoint-landscape"
          sx={landscapeScrollContainer(theme)}
        >
          <Box
            component="table"
            aria-label={definition.prompt}
            sx={landscapeTable(definition.concepts.length, theme)}
          >
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  aria-hidden="true"
                  sx={landscapeRowHeader(theme)}
                />
                {definition.concepts.map((concept, index) => (
                  <Box
                    component="th"
                    scope="col"
                    key={concept.id}
                    aria-label={
                      concept.displayLabel
                        ? undefined
                        : formatCopy(theme.copy.labels.option, {
                            index: index + 1,
                          })
                    }
                    sx={{
                      backgroundColor: theme.tokens.color.surface.card,
                      border: `1px solid ${theme.tokens.color.border.default}`,
                    }}
                  >
                    {concept.displayLabel && (
                      <Box sx={landscapeConceptHeader(theme)}>
                        <Typography component="span">
                          {concept.label}
                        </Typography>
                      </Box>
                    )}
                    {conceptMedia(index)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {definition.attributes.map((attribute) => (
                <Box component="tr" key={attribute.id}>
                  <Box
                    component="th"
                    scope="row"
                    sx={landscapeRowHeader(theme)}
                  >
                    <Hints
                      text={attribute.label}
                      variant="label"
                      adaptiveText={{
                        maxLines: 3,
                        minimumSize: "xs",
                      }}
                      sx={{
                        color: "inherit",
                        fontWeight: "inherit",
                        textAlign: "inherit",
                      }}
                      testId={`conjoint-attribute-label-${attribute.id}`}
                    />
                  </Box>
                  {definition.concepts.map((concept, index) => (
                    <Box
                      component="td"
                      key={concept.id}
                      sx={landscapeCell(theme)}
                    >
                      {attribute.values[index]}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            <Box component="tfoot">
              <Box component="tr">
                <Box
                  component="td"
                  aria-hidden="true"
                  sx={landscapeRowHeader(theme)}
                />
                {definition.concepts.map((concept, index) => (
                  <Box
                    component="td"
                    key={concept.id}
                    sx={landscapeCell(theme)}
                  >
                    {choose(index)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          role="list"
          aria-label={theme.copy.labels.conjointOptions}
          data-testid="conjoint-portrait"
          sx={portraitCards}
        >
          {definition.concepts.map((concept, index) => (
            <Box
              component="article"
              role="listitem"
              key={concept.id}
              aria-labelledby={
                concept.displayLabel
                  ? `conjoint-option-title-${index}`
                  : undefined
              }
              aria-label={
                concept.displayLabel
                  ? undefined
                  : formatCopy(theme.copy.labels.option, { index: index + 1 })
              }
              sx={card(theme)}
            >
              {concept.displayLabel && (
                <Box
                  id={`conjoint-option-title-${index}`}
                  sx={landscapeConceptHeader(theme)}
                >
                  {concept.label}
                </Box>
              )}
              {conceptMedia(index)}
              {definition.attributes.map((attribute) => (
                <Box sx={cardAttribute(theme)} key={attribute.id}>
                  <Typography component="div" sx={cardAttributeLabel(theme)}>
                    {attribute.label}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    {attribute.values[index]}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ padding: theme.tokens.spacing["3"] / 8 }}>
                {choose(index)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {(definition.none || definition.dontKnow) && (
        <Box sx={fixedOptionsContainer}>
          {[definition.none, definition.dontKnow]
            .filter(
              (option): option is NonNullable<typeof option> =>
                option !== undefined,
            )
            .map((option) => (
              <ActionButton
                key={option.value}
                currentVal={{
                  value: option.value,
                  text: option.label,
                  display_text: true,
                }}
                testId={
                  option === definition.none
                    ? "conjoint-none-option"
                    : "conjoint-dont-know-option"
                }
                disabled={!props.controls.canSubmit}
                isSelected={draft.selectedId === option.value}
                ariaLabel={option.label}
                ariaPressed={draft.selectedId === option.value}
                onClick={() =>
                  props.submit({ type: "select", value: option.value })
                }
              />
            ))}
        </Box>
      )}
    </Box>
  );
};
